import { FC } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { LdtOrderRepository } from '@ldt/api/order_repository';
import { PathologyLabAutocomplete } from '@ldt/features/orders/components/pathology_lab_autocomplete';
import EmailIcon from 'src/assets/email.svg';
import FaxIcon from 'src/assets/fax.svg';
import SkipFaxIcon from 'src/assets/fax_2.svg';
import LaboratoryIcon from 'src/assets/laboratory.svg';
import MapIcon from 'src/assets/map.svg';
import PhoneIcon from 'src/assets/phone.svg';
import { FieldList } from 'src/components/field_list';
import { useWorkflowType } from 'src/distribution_platforms/features/hooks/use_workflow';
import { useIsLdtSalesOrCustomerSuccessUser } from 'src/hooks/use_auth';
import {
  LdtOrderDetailFieldsFragment,
  OrderStatus,
  PathologyLabFieldsFragment,
  PathologyLabType,
} from 'src/types';
import { WorkflowType } from 'src/types/workflows';
import { formatAddress } from 'src/utils/address_utils';

const IconImage = styled('img')({
  width: 20,
  height: 20,
});

interface PathologyLabSectionProps {
  order: LdtOrderDetailFieldsFragment;
  orderRepository: LdtOrderRepository;
  onPathologyLabChange: (
    pathologyLab: PathologyLabFieldsFragment | null,
  ) => void;
}

/**
 * Pathology Lab Section Component
 *
 * Displays pathology lab selector (when user can add/edit) and pathology lab information
 * in a single column layout using FieldList (when pathology lab is selected).
 */
export const PathologyLabSection: FC<PathologyLabSectionProps> = ({
  order,
  orderRepository,
  onPathologyLabChange,
}) => {
  const pathologyLab = order.pathologyLab;
  const isCSOrSalesUser = useIsLdtSalesOrCustomerSuccessUser();
  const workflowType = useWorkflowType();
  const isQCPending = order.status === OrderStatus.QualityControlPending;
  const canAddPL =
    isCSOrSalesUser &&
    ((isQCPending && workflowType === WorkflowType.CLINIC) ||
      ([
        OrderStatus.QualityControlPending,
        OrderStatus.QualityControlApproved,
        OrderStatus.LabProcessing,
      ].includes(order.status) &&
        workflowType === WorkflowType.TEMPUS));
  const canViewPLInformation = pathologyLab != null;
  const isDirectUploadPathologyLab =
    pathologyLab?.type === PathologyLabType.DirectUpload;

  const fields = [
    {
      label: 'Lab Name',
      value: pathologyLab?.name,
      icon: <IconImage src={LaboratoryIcon} alt='Laboratory' />,
    },
    {
      label: 'Phone',
      value: pathologyLab?.phoneNumber,
      icon: <IconImage src={PhoneIcon} alt='Phone' />,
    },
    {
      label: 'Fax',
      value: pathologyLab?.faxNumber,
      icon: <IconImage src={FaxIcon} alt='Fax' />,
    },
    {
      label: 'Email',
      value: pathologyLab?.email,
      icon: <IconImage src={EmailIcon} alt='Email' />,
    },
    {
      label: 'Address',
      value: pathologyLab
        ? formatAddress(
            pathologyLab.street,
            pathologyLab.city,
            pathologyLab.state,
            pathologyLab.zipCode,
          ) || undefined
        : undefined,
      icon: <IconImage src={MapIcon} alt='Map' />,
    },
    {
      label: 'Skip Fax',
      value: pathologyLab ? (pathologyLab.skipFax ? 'Yes' : 'No') : undefined,
      icon: <IconImage src={SkipFaxIcon} alt='Skip Fax' />,
    },
  ];

  return (
    <Box>
      {canAddPL && (
        <Box sx={{ mb: 2 }}>
          <PathologyLabAutocomplete
            orderRepository={orderRepository}
            value={pathologyLab}
            onChange={onPathologyLabChange}
            label='Pathology Lab'
          />
        </Box>
      )}
      {isDirectUploadPathologyLab && (
        <Typography
          variant='body2'
          sx={{
            color: 'text.secondary',
            mb: canViewPLInformation ? 2 : 0,
          }}
        >
          This pathology lab will directly upload images to the portal and will
          not send them to the OPD.
        </Typography>
      )}
      {canViewPLInformation && <FieldList fields={fields} />}
    </Box>
  );
};
